import React, { useEffect, useMemo, useState } from "react";
import { apiClient } from "@/lib/apiClient";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Split, Trash2, Save, Loader2, Plus } from "lucide-react";

const SOURCE = {
  learned: { label: "gelernt", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  rule: { label: "Regel", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  purpose: { label: "Zweck", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  keyword_note: { label: "Regel", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  keyword_text: { label: "Regel", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  ai: { label: "KI", cls: "bg-violet-50 text-violet-700 border-violet-200" },
  header: { label: "Kopf-Konto", cls: "bg-slate-50 text-slate-600 border-slate-200" },
  manual: { label: "manuell", cls: "bg-sky-50 text-sky-700 border-sky-200" },
};
const fmt = (v) => Number(v || 0).toLocaleString("de-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const cell = "w-full rounded border border-slate-300 bg-white px-1.5 py-0.5 text-[11px] text-slate-800";

/** Positionen kontieren: Konto je Document-AI-Position, Schalter Aufteilen ↔ Ein Konto. */
export function InboxSplitPanel({ companyId, item, accounts, ensureAccounts, onUpdated }) {
  const ex = item.extracted || {};
  const split = ex.account_split;
  const [enabled, setEnabled] = useState(!!split?.enabled);
  const [lines, setLines] = useState(split?.lines || []);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setEnabled(!!split?.enabled); setLines(split?.lines || []); setDirty(false); }, [split]);
  useEffect(() => { if (enabled) ensureAccounts?.(); }, [enabled, ensureAccounts]);

  const sum = useMemo(() => lines.reduce((s, l) => s + Number(l.amount || 0), 0), [lines]);
  const ref = ex.net_amount != null ? { label: "Netto", value: Number(ex.net_amount) } : ex.total_amount != null ? { label: "Brutto", value: Number(ex.total_amount) } : null;
  const refNet = ex.net_amount != null ? Number(ex.net_amount) : null;
  const refGross = ex.total_amount != null ? Number(ex.total_amount) : null;
  const matches = [refNet, refGross].some((v) => v != null && Math.abs(v - sum) <= 0.06);
  const distinct = new Set(lines.filter((l) => l.account).map((l) => l.account)).size;

  if (!split || !(split.lines || []).length) return null;

  const upd = (i, patch) => { setLines(lines.map((l, k) => (k === i ? { ...l, ...patch, source: patch.account !== undefined ? "manual" : l.source } : l))); setDirty(true); };
  const removeLine = (i) => { setLines(lines.filter((_, k) => k !== i)); setDirty(true); };
  const addLine = () => { setLines([...lines, { description: "", amount: 0, account: null, source: "manual" }]); setDirty(true); };

  const save = async (nextEnabled = enabled) => {
    setBusy(true);
    try {
      const r = await apiClient.put(`/companies/${companyId}/inbox/${item.id}/account-split`, { enabled: nextEnabled, lines });
      onUpdated?.({ ...item, extracted: r.data.extracted, checks: r.data.checks, routing: r.data.routing });
      toast.success(nextEnabled ? `Split gespeichert: ${r.data.account_split.lines.length} Positionen auf ${r.data.account_split.distinct_accounts} Konten` : "Ein Konto – Split deaktiviert");
      setDirty(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Split konnte nicht gespeichert werden"); } finally { setBusy(false); }
  };

  const toggle = async (v) => { setEnabled(v); await save(v); };

  return (
    <div className={`mt-1.5 rounded border p-2 text-[11px] ${enabled ? "border-teal-300 bg-teal-50/50" : "border-slate-200 bg-slate-50"}`} data-testid={`inbox-split-${item.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Switch checked={enabled} onCheckedChange={toggle} disabled={busy} className="scale-75" data-testid={`inbox-split-toggle-${item.id}`} />
          <span className="inline-flex items-center gap-1 font-medium text-slate-800"><Split className="h-3.5 w-3.5 text-teal-600" /> Positionen kontieren</span>
          <span className="text-slate-500" data-testid={`inbox-split-summary-${item.id}`}>{lines.length} Positionen → {distinct} {distinct === 1 ? "Konto" : "Konten"}</span>
          {!enabled && <span className="text-slate-500">· Ein Konto: <span className="font-mono">{ex.suggested_account || "–"}</span></span>}
        </div>
        {enabled && (
          <span className={`rounded border px-1.5 py-px ${matches ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-rose-300 bg-rose-50 text-rose-800"}`} data-testid={`inbox-split-sum-${item.id}`}>
            Positionen {fmt(sum)}{ref ? ` · ${ref.label} ${fmt(ref.value)}` : ""}{matches ? " ✓" : " ≠"}
          </span>
        )}
      </div>
      {enabled && (
        <div className="mt-1.5 space-y-1">
          <div className="grid grid-cols-[1fr_90px_minmax(180px,1.2fr)_70px_22px] gap-1 text-[10px] text-slate-500 px-0.5">
            <span>Position</span><span className="text-right">Betrag</span><span>Konto</span><span>Quelle</span><span />
          </div>
          {lines.map((l, i) => {
            const src = SOURCE[l.source] || SOURCE.manual;
            return (
              <div key={i} className="grid grid-cols-[1fr_90px_minmax(180px,1.2fr)_70px_22px] items-center gap-1" data-testid={`inbox-split-line-${item.id}-${i}`}>
                <input className={cell} value={l.description || ""} onChange={(e) => upd(i, { description: e.target.value })} data-testid={`split-desc-${i}`} />
                <input className={`${cell} text-right font-mono`} type="number" step="0.05" value={l.amount ?? ""} onChange={(e) => upd(i, { amount: e.target.value === "" ? "" : Number(e.target.value) })} data-testid={`split-amount-${i}`} />
                <select className={cell} value={l.account || ""} onChange={(e) => upd(i, { account: e.target.value || null })} data-testid={`split-account-${i}`}>
                  <option value="">– Konto wählen –</option>
                  {l.account && !(accounts || []).some((a) => String(a.code) === String(l.account)) && <option value={l.account}>{l.account_label || l.account}</option>}
                  {(accounts || []).map((a) => <option key={a.code} value={a.code}>{a.code} {a.name}</option>)}
                </select>
                <span className={`rounded border px-1 py-px text-center text-[10px] ${src.cls}`} title={l.confidence ? `Vertrauen: ${l.confidence}` : ""}>{src.label}</span>
                <button type="button" onClick={() => removeLine(i)} className="text-slate-400 hover:text-rose-600" title="Position entfernen" data-testid={`split-remove-${i}`}><Trash2 className="h-3 w-3" /></button>
              </div>
            );
          })}
          <div className="flex items-center justify-between pt-0.5">
            <button type="button" onClick={addLine} className="inline-flex items-center gap-1 text-slate-600 hover:text-slate-900" data-testid={`inbox-split-add-${item.id}`}><Plus className="h-3 w-3" /> Position</button>
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-slate-500">Beim Bestätigen: eine Soll-Zeile pro Konto, Vorsteuer anteilig, Beträge auf Netto normiert. Split wird nie automatisch verbucht.</span>
              <button type="button" onClick={() => save(true)} disabled={busy || !dirty} className="inline-flex items-center gap-1 rounded bg-teal-700 px-2 py-0.5 font-medium text-white hover:bg-teal-800 disabled:opacity-40" data-testid={`inbox-split-save-${item.id}`}>
                {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />} Split speichern
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
